import React from 'react';
import { repairPwaAndReload } from '@/lib/pwaRepair';

/**
 * ErrorBoundary a livello ROOT dell'app.
 *
 * Cattura qualsiasi crash React non gestito e mostra una schermata di
 * recovery user-friendly INVECE di lasciare l'utente con schermo blu/nero
 * (tipico problema PWA quando l'app crasha).
 *
 * Contiene 4 azioni:
 * - Ricarica pagina (window.location.reload)
 * - Ritorna alla dashboard
 * - Cancella cache PWA (service worker + caches API) + reload
 * - Copia dettagli errore per invio a supporto
 *
 * Nota: usa CSS inline invece di Tailwind perché il crash potrebbe
 * essere avvenuto proprio nel caricamento degli stili.
 */
export class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RootErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
    // Best-effort logging al backend per audit (senza bloccare)
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      if (url) {
        fetch(`${url}/api/client-errors`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: String(error?.message || error),
            stack: String(error?.stack || ''),
            component_stack: String(errorInfo?.componentStack || ''),
            severity: 'critical',
            location: window.location.href,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      }
    } catch (_) { /* silent */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearCache = async () => {
    await repairPwaAndReload();
  };

  handleCopyDetails = () => {
    const err = this.state.error;
    const info = this.state.errorInfo;
    const details = [
      `URL: ${window.location.href}`,
      `Errore: ${err?.message || String(err)}`,
      `Stack:\n${err?.stack || ''}`,
      `Component stack:\n${info?.componentStack || ''}`,
      `UA: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
    ].join('\n\n');
    try {
      navigator.clipboard.writeText(details);
      alert('Dettagli errore copiati nella clipboard. Incolla nella chat di supporto.');
    } catch (_) {
      alert(details);
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif',
      }} data-testid="root-error-boundary">
        <div style={{
          maxWidth: 640, width: '100%', background: 'white', border: '1px solid #e2e8f0',
          borderRadius: 12, padding: 32, boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 20, background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <span style={{ fontSize: 20, color: '#dc2626' }}>!</span>
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                Qualcosa è andato storto
              </h1>
              <p style={{ fontSize: 14, color: '#475569', margin: '4px 0 0' }}>
                L&apos;app si è bloccata inaspettatamente. I tuoi dati sono al sicuro. Prova una delle opzioni sotto.
              </p>
            </div>
          </div>

          <details style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: '#64748b', userSelect: 'none' }}>
              Dettagli tecnici
            </summary>
            <pre style={{
              margin: '8px 0 0', fontSize: 11, fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#334155',
              maxHeight: 200, overflow: 'auto',
            }}>{msg}</pre>
          </details>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={this.handleReload} style={buttonPrimary} data-testid="err-reload">
              Ricarica pagina
            </button>
            <button onClick={this.handleGoHome} style={buttonSecondary} data-testid="err-home">
              Vai alla home
            </button>
            <button onClick={this.handleClearCache} style={buttonSecondary} data-testid="err-clear-cache">
              Ripara cache PWA
            </button>
            <button onClick={this.handleCopyDetails} style={buttonGhost} data-testid="err-copy">
              Copia dettagli
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 16 }}>
            Se il problema persiste, usa &quot;Ripara cache PWA&quot; e poi ricarica.
          </p>
        </div>
      </div>
    );
  }
}

const buttonBase = {
  padding: '10px 18px', fontSize: 14, fontWeight: 500, borderRadius: 8,
  border: 'none', cursor: 'pointer', transition: 'opacity 0.15s',
};
const buttonPrimary = { ...buttonBase, background: '#0f172a', color: 'white' };
const buttonSecondary = { ...buttonBase, background: '#e2e8f0', color: '#0f172a' };
const buttonGhost = { ...buttonBase, background: 'transparent', color: '#64748b' };

export default RootErrorBoundary;
